import { useState } from "react";
import { api } from "../api/staff";

type CreditPlan = "pro" | "max";

const PLAN_OPTIONS: { value: CreditPlan; label: string; desc: string }[] = [
  { value: "pro", label: "Pro", desc: "50 crédits/24h — ×1" },
  { value: "max", label: "Max ×10", desc: "500 crédits/24h — ×10" },
];

const DURATION_OPTIONS = [
  { days: 7, label: "7 jours" },
  { days: 30, label: "30 jours" },
  { days: 90, label: "90 jours" },
  { days: 180, label: "180 jours" },
  { days: 365, label: "1 an" },
];

export function UserGrant() {
  const [publicId, setPublicId] = useState("");
  const [plan, setPlan] = useState<CreditPlan>("pro");
  const [days, setDays] = useState(30);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!publicId.trim()) { setError("Public ID requis"); return; }
    if (!reason.trim()) { setError("Raison requise"); return; }
    setLoading(true);
    try {
      const data = await api<{ outcome: { subscriptionId: string; currentPeriodEnd: string } }>(
        `/staff/admin/users/${publicId.trim()}/grant-subscription`,
        { method: "POST", body: JSON.stringify({ creditPlan: plan, durationDays: days, reason }) },
      );
      setResult(
        `✓ Abonnement accordé — ID ${data.outcome.subscriptionId} — expire le ${new Date(data.outcome.currentPeriodEnd).toLocaleDateString("fr-FR")}`,
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6 max-w-lg">
      <div>
        <h1 className="text-2xl font-semibold">Accorder un abonnement</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Attribution gratuite d'un plan pro ou max à un utilisateur.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-5">
        <div>
          <label className="block text-sm font-medium mb-1">Public ID utilisateur</label>
          <input
            type="text"
            value={publicId}
            onChange={(e) => setPublicId(e.target.value)}
            placeholder="61542734326042624"
            className="w-full rounded-lg border border-border bg-bg-subtle px-3 py-2 text-sm outline-none focus:border-accent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Plan</label>
          <div className="flex gap-3">
            {PLAN_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPlan(opt.value)}
                className={`flex-1 rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
                  plan === opt.value
                    ? "border-accent bg-accent/10 text-accent"
                    : "border-border bg-bg-subtle text-text-secondary hover:border-accent/40"
                }`}
              >
                <p className="font-semibold">{opt.label}</p>
                <p className="mt-0.5 text-[11px] opacity-70">{opt.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Durée</label>
          <div className="flex flex-wrap gap-2">
            {DURATION_OPTIONS.map((opt) => (
              <button
                key={opt.days}
                type="button"
                onClick={() => setDays(opt.days)}
                className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                  days === opt.days
                    ? "border-accent bg-accent/10 text-accent"
                    : "border-border bg-bg-subtle text-text-secondary hover:border-accent/40"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Raison (audit)</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="ex: compte de test interne"
            className="w-full rounded-lg border border-border bg-bg-subtle px-3 py-2 text-sm outline-none focus:border-accent"
          />
        </div>

        {error && <p className="text-sm text-red-400">{error}</p>}
        {result && <p className="text-sm text-green-400">{result}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-bg disabled:opacity-50"
        >
          {loading ? "Envoi…" : "Accorder l'abonnement"}
        </button>
      </form>
    </div>
  );
}
